import { debugLog } from '../../../utils/logger';
import { preprocessTrainingData } from '../utils/MachineLearningUtils';
import { modelConfigs } from '../utils/MLModelConfig';
import TrainingAnalyzer from '../TrainingAnalyzer';

/**
 * MLMonitor - Utility di monitoraggio per le funzionalità di Machine Learning
 * Da usare solo in ambiente di sviluppo per verificare se ML è attivo e funzionante
 */
export class MLMonitor {
  /**
   * Verifica lo stato del ML nell'applicazione
   * @param {Array} workouts - Allenamenti dell'utente
   * @param {Array} sets - Serie dell'utente 
   * @returns {Object} Stato e dettagli del ML
   */
  static checkMLStatus(workouts = [], sets = []) {
    //debugLog('🧠 MLMonitor: Verifica dello stato del Machine Learning...');
    
    // Controlla dati minimi richiesti
    const hasMinimumData = workouts.length >= 9 && sets.length >= 50;
    //debugLog(`📊 Dati disponibili: ${workouts.length} allenamenti, ${sets.length} serie`);
    //debugLog(`📊 Dati minimi richiesti: 10 allenamenti, 50 serie`);
    
    // Modelli ML configurati
    const availableModels = Object.keys(modelConfigs);
    //debugLog(`📋 Modelli ML configurati: ${availableModels.join(', ')}`);
    
    // Simula preprocessing dati
    const preprocessStartTime = performance.now();
    const preprocessedData = preprocessTrainingData({ workouts, sets });
    const preprocessEndTime = performance.now();
    const preprocessingTime = (preprocessEndTime - preprocessStartTime).toFixed(2);
    
    //debugLog(`⏱️ Tempo di preprocessing: ${preprocessingTime}ms`);
    
    const featuresGenerated = {
      workoutFeatures: preprocessedData.workoutFeatures.length,
      exerciseFeatures: preprocessedData.exerciseFeatures.length,
      timeSeriesData: preprocessedData.timeSeriesData.length
    };
    
    //debugLog(`🔢 Features generate: `, featuresGenerated);
    
    // Verifica features richieste per i modelli
    const featuresCheck = {};
    
    for (const [modelName, config] of Object.entries(modelConfigs)) {
      const requiredFeatures = config.features || [];
      const hasAllFeatures = requiredFeatures.every(feature => 
        this.checkFeatureAvailability(feature, preprocessedData)
      );
      
      featuresCheck[modelName] = {
        hasAllFeatures,
        requiredFeatures,
        missingFeatures: requiredFeatures.filter(feature => 
          !this.checkFeatureAvailability(feature, preprocessedData)
        )
      };
    }
    
    // Stato complessivo ML
    const mlStatus = {
      isActive: hasMinimumData,
      dataQuality: this.assessDataQuality(workouts.length, sets.length),
      availableModels,
      featuresGenerated,
      featuresCheck,
      suggestions: hasMinimumData ? 'disponibili' : 'non disponibili',
      estimatedAccuracy: hasMinimumData ? 
        (workouts.length >= 20 ? 'alta' : 'media') : 'bassa'
    };
    
    //debugLog('🧠 Stato ML: ', mlStatus.isActive ? 'ATTIVO' : 'NON ATTIVO');
    
    return mlStatus;
  }
  
  /**
   * Verifica la disponibilità di una feature nei dati preprocessati
   * @param {string} feature - Nome della feature
   * @param {Object} preprocessedData - Dati preprocessati
   * @returns {boolean} True se la feature è disponibile
   */
  static checkFeatureAvailability(feature, preprocessedData) {
    // Controllo semplificato - in una versione reale sarebbe più complesso
    if (preprocessedData.workoutFeatures.length > 0 && 
        preprocessedData.workoutFeatures[0][feature] !== undefined) {
      return true;
    }
    
    if (preprocessedData.exerciseFeatures.length > 0) {
      if (preprocessedData.exerciseFeatures[0][feature] !== undefined) {
        return true;
      }
      
      // Controlla nelle sotto-proprietà
      for (const obj of preprocessedData.exerciseFeatures) {
        if (obj.progression && obj.progression[feature] !== undefined) return true;
        if (obj.frequency && obj.frequency[feature] !== undefined) return true;
      }
    }
    
    if (preprocessedData.timeSeriesData.length > 0 &&
        preprocessedData.timeSeriesData[0][feature] !== undefined) {
      return true;
    }
    
    return false;
  }
  
  /**
   * Valuta la qualità dei dati disponibili
   * @param {number} workoutsCount - Numero di allenamenti
   * @param {number} setsCount - Numero di serie
   * @returns {string} Livello di qualità dei dati
   */
  static assessDataQuality(workoutsCount, setsCount) {
    if (workoutsCount < 10 || setsCount < 50) return 'insufficiente';
    if (workoutsCount < 20 || setsCount < 100) return 'base';
    if (workoutsCount < 40 || setsCount < 200) return 'buona';
    return 'eccellente';
  }
  
  /**
   * Testa le funzionalità ML attraverso TrainingAnalyzer
   * @param {Array} workouts - Allenamenti dell'utente
   * @param {Array} sets - Serie dell'utente
   * @returns {Object} Risultati dei test
   */
  static async testMLFunctionality(workouts = [], sets = []) {
    //debugLog('🧪 MLMonitor: Test funzionalità ML in corso...');
    
    const testResults = {
      mlStatus: this.checkMLStatus(workouts, sets),
      initializationTest: null,
      analyzerTests: {},
      personalizedSuggestions: null
    };
    
    // Se non ci sono dati sufficienti, termina qui
    if (!testResults.mlStatus.isActive) {
     //debugLog('⚠️ Dati insufficienti per i test ML');
      return testResults;
    }
    
    // Test inizializzazione MLAnalyzer
    try {
      //debugLog('🚀 Test inizializzazione MLAnalyzer...');
      const startTime = performance.now();
      const mlAnalyzer = await TrainingAnalyzer.initializeMLAnalyzer(workouts, sets);
      const endTime = performance.now();
      
      testResults.initializationTest = {
        success: !!mlAnalyzer,
        time: (endTime - startTime).toFixed(2) + 'ms',
        analyzer: mlAnalyzer ? 'inizializzato' : 'fallito'
      };
      
      //debugLog(`✅ Inizializzazione ${testResults.initializationTest.success ? 'riuscita' : 'fallita'} in ${testResults.initializationTest.time}`);
      
      // Se l'inizializzazione è riuscita, testa altre funzionalità
      if (mlAnalyzer) {
        // Test pattern detection
        testResults.analyzerTests.patternDetection = {
          patterns: mlAnalyzer.workoutPatterns.length,
          success: mlAnalyzer.workoutPatterns.length > 0
        };
        
        // Test metriche utente
        const hasMetrics = Object.values(mlAnalyzer.userMetrics).some(v => v !== null);
        testResults.analyzerTests.userMetrics = {
          available: hasMetrics,
          metrics: mlAnalyzer.userMetrics
        };
        
        // Test suggerimenti personalizzati
        const suggestions = mlAnalyzer.generatePersonalizedSuggestions();
        testResults.personalizedSuggestions = {
          count: suggestions.length,
          types: suggestions.map(s => s.type),
          available: suggestions.length > 0
        };
        
        //debugLog(`📊 Pattern rilevati: ${testResults.analyzerTests.patternDetection.patterns}`);
        //debugLog(`👤 Metriche utente disponibili: ${testResults.analyzerTests.userMetrics.available}`);
        //debugLog(`💡 Suggerimenti generati: ${testResults.personalizedSuggestions.count}`);
      }
    } catch (error) {
      //debugLog('❌ Errore durante il test di inizializzazione:', error);
      testResults.initializationTest = {
        success: false,
        error: error.message
      };
    }
    
    // Test report con ML
    try {
      //debugLog('📈 Test generazione report con ML...');
      const startTime = performance.now();
      const report = await TrainingAnalyzer.generateTrainingReportWithML({
        workouts, sets
      });
      const endTime = performance.now();
      
      testResults.analyzerTests.mlReport = {
        success: !!report,
        time: (endTime - startTime).toFixed(2) + 'ms',
        hasMLAnalysis: !!report?.mlAnalysis,
        suggestions: report?.mlAnalysis?.suggestions?.length || 0
      };
      
      //debugLog(`✅ Generazione report ${testResults.analyzerTests.mlReport.success ? 'riuscita' : 'fallita'} in ${testResults.analyzerTests.mlReport.time}`);
      //debugLog(`📊 Report contiene analisi ML: ${testResults.analyzerTests.mlReport.hasMLAnalysis}`);
    } catch (error) {
      //debugLog('❌ Errore durante la generazione del report:', error);
      testResults.analyzerTests.mlReport = {
        success: false,
        error: error.message
      };
    }
    
    return testResults;
  }
  
  /**
   * Esegue test su suggerimenti personalizzati
   * @param {Array} workouts - Allenamenti dell'utente 
   * @param {Array} sets - Serie dell'utente
   * @returns {Object} Risultati dei test sui suggerimenti
   */
  static async testSuggestions(workouts = [], sets = []) {
    //debugLog('💡 MLMonitor: Test suggerimenti personalizzati...');
    
    const testResults = {
      standardSuggestions: null,
      personalizedSuggestions: null,
      mlSuggestions: null,
      comparison: null
    };
    
    try {
      // Genera suggerimenti standard
      const standardStartTime = performance.now();
      const standardSuggestions = TrainingAnalyzer.generateTrainingSuggestions(workouts, sets);
      const standardEndTime = performance.now();
      
      testResults.standardSuggestions = {
        count: standardSuggestions.length,
        time: (standardEndTime - standardStartTime).toFixed(2) + 'ms',
        types: this.countSuggestionTypes(standardSuggestions)
      };
      
      //debugLog(`💡 Suggerimenti standard: ${testResults.standardSuggestions.count} in ${testResults.standardSuggestions.time}`);
      
      // Genera suggerimenti personalizzati tramite TrainingAnalyzer
      const personalizedStartTime = performance.now();
      const personalizedSuggestions = TrainingAnalyzer.generatePersonalizedTrainingSuggestions(
        workouts, 
        sets, 
        { experienceLevel: 'intermediate' }
      );
      const personalizedEndTime = performance.now();
      
      testResults.personalizedSuggestions = {
        count: personalizedSuggestions.length,
        time: (personalizedEndTime - personalizedStartTime).toFixed(2) + 'ms',
        types: this.countSuggestionTypes(personalizedSuggestions)
      };
      
      //debugLog(`💡 Suggerimenti personalizzati: ${testResults.personalizedSuggestions.count} in ${testResults.personalizedSuggestions.time}`);
      
      // Genera suggerimenti ML specifici
      const mlStartTime = performance.now();
      let mlSuggestions = [];
      
      try {
        mlSuggestions = await TrainingAnalyzer.generateMLSuggestions(
          workouts, 
          sets, 
          { experienceLevel: 'intermediate' }
        );
      } catch (error) {
        //debugLog('⚠️ Errore generando suggerimenti ML:', error);
      }
      
      const mlEndTime = performance.now();
      
      testResults.mlSuggestions = {
        count: mlSuggestions.length,
        time: (mlEndTime - mlStartTime).toFixed(2) + 'ms',
        types: this.countSuggestionTypes(mlSuggestions),
        available: mlSuggestions.length > 0
      };
      
      //debugLog(`💡 Suggerimenti ML: ${testResults.mlSuggestions.count} in ${testResults.mlSuggestions.time}`);
      
      // Confronta i diversi tipi di suggerimenti
      testResults.comparison = {
        standardCount: testResults.standardSuggestions.count,
        personalizedCount: testResults.personalizedSuggestions.count,
        mlCount: testResults.mlSuggestions.count,
        totalUnique: new Set([
          ...standardSuggestions.map(s => s.title),
          ...personalizedSuggestions.map(s => s.title),
          ...mlSuggestions.map(s => s.title)
        ]).size
      };
      
      //debugLog('✅ Test suggerimenti completato');
    } catch (error) {
      //debugLog('❌ Errore durante il test dei suggerimenti:', error);
      testResults.error = error.message;
    }
    
    return testResults;
  }
  
  /**
   * Conta i tipi di suggerimenti
   * @param {Array} suggestions - Suggerimenti
   * @returns {Object} Conteggio dei tipi
   */
  static countSuggestionTypes(suggestions) {
    const types = {};
    
    suggestions.forEach(suggestion => {
      const type = suggestion.type || 'unknown';
      types[type] = (types[type] || 0) + 1;
    });
    
    return types;
  }
}

export default MLMonitor;